import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, eur, formatApiErrorDetail, RATE_CODES, RATE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Lock, Trash2, ShieldCheck, ScanLine, Download, BookText } from "lucide-react";
import MissingDaysDialog from "@/components/MissingDaysDialog";

const emptyRates = () => ({ "0": "", "6": "", "21": "", "E": "" });

export function EntryForm({ locations, defaults = {}, onCreated }) {
  const [open, setOpen] = useState(false);
  const [locId, setLocId] = useState(defaults.location_id || "");
  const [date, setDate] = useState(defaults.date || new Date().toISOString().slice(0, 10));
  const [type, setType] = useState("receipts");
  const [rates, setRates] = useState(defaults.gross_by_rate || emptyRates());
  const [kranten, setKranten] = useState("");
  const metKranten = locations.find((l) => l.id === locId)?.category_scheme === "leesberg_5";
  const [reason, setReason] = useState("gesloten");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [gapInfo, setGapInfo] = useState(null);

  const submit = async (fillGaps = false) => {
    if (!locId) return toast.error("Kies een vestiging");
    setSaving(true);
    try {
      const gross_by_rate = {};
      RATE_CODES.forEach((r) => (gross_by_rate[r] = parseFloat(rates[r]) || 0));
      await api.post(`/journal${fillGaps ? "?fill_gaps=true" : ""}`, {
        location_id: locId, date, entry_type: type, gross_by_rate,
        none_reason: type === "none" ? reason : null, note,
        high_value_items: items.map((i) => ({ ...i, amount: parseFloat(i.amount) || 0, vat_rate: parseInt(i.vat_rate) })),
        source: "manual",
        kranten: metKranten ? parseFloat(kranten) || 0 : 0,
      });
      toast.success(fillGaps ? "Overgeslagen dag(en) als GESLOTEN (€0) geboekt en boeking geregistreerd" : "Boeking definitief geregistreerd (onveranderlijk)");
      setOpen(false); setGapInfo(null);
      setRates(emptyRates()); setItems([]); setNote(""); setKranten("");
      onCreated && onCreated();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 409 && det && det.error === "gap") {
        setGapInfo(det);
      } else {
        toast.error(formatApiErrorDetail(det));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-entry-form"><Plus className="h-4 w-4 mr-2" /> Nieuwe dagontvangst</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Dagontvangst boeken</DialogTitle>
          <DialogDescription>Registreer de dagontvangsten per BTW-tarief. Na registratie is de boeking onveranderlijk.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Vestiging</Label>
              <Select value={locId} onValueChange={setLocId}>
                <SelectTrigger data-testid="entry-location"><SelectValue placeholder="Kies vestiging" /></SelectTrigger>
                <SelectContent>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ed">Datum</Label>
              <Input id="ed" type="date" data-testid="entry-date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant={type === "receipts" ? "default" : "outline"} size="sm"
                    data-testid="type-receipts" onClick={() => setType("receipts")}>Ontvangsten</Button>
            <Button type="button" variant={type === "none" ? "default" : "outline"} size="sm"
                    data-testid="type-none" onClick={() => setType("none")}>Geen ontvangsten</Button>
          </div>

          {type === "receipts" ? (
            <div className="space-y-3">
              <Label>Ontvangsten per BTW-tarief (bedrag incl. BTW)</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {RATE_CODES.map((r) => (
                  <div key={r} className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{RATE_LABELS[r]}</Label>
                    <Input type="number" step="0.01" data-testid={`rate-${r}`} value={rates[r]}
                           onChange={(e) => setRates({ ...rates, [r]: e.target.value })} placeholder="0,00" />
                  </div>
                ))}
              </div>
              {metKranten && (
                <div className="space-y-1 max-w-xs">
                  <Label className="text-xs text-muted-foreground">Waarvan Kranten (deel van het 0%-bedrag)</Label>
                  <Input type="number" step="0.01" data-testid="rate-kranten" value={kranten}
                         onChange={(e) => setKranten(e.target.value)} placeholder="0,00" />
                </div>
              )}

              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Goederen &gt; € 250 (aard + bewijsstuk)</Label>
                  <Button type="button" size="sm" variant="outline" data-testid="add-item"
                          onClick={() => setItems([...items, { description: "", amount: "", vat_rate: 21, invoice_number: "", document_ref: "" }])}>
                    <Plus className="h-3 w-3 mr-1" /> Item
                  </Button>
                </div>
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-4" placeholder="Aard van het goed" value={it.description}
                           onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} />
                    <Input className="col-span-2" type="number" placeholder="Bedrag" value={it.amount}
                           onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} />
                    <Input className="col-span-3" placeholder="Factuurnr." value={it.invoice_number}
                           onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, invoice_number: e.target.value } : x))} />
                    <Input className="col-span-2" placeholder="Ref." value={it.document_ref}
                           onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, document_ref: e.target.value } : x))} />
                    <Button type="button" variant="ghost" size="icon" className="col-span-1"
                            onClick={() => setItems(items.filter((_, i) => i !== idx))}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Reden (verplicht)</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="none-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gesloten">Winkel gesloten</SelectItem>
                  <SelectItem value="verlof">Verlof</SelectItem>
                  <SelectItem value="geen_ontvangsten">Nul-ontvangst (open, geen omzet)</SelectItem>
                  <SelectItem value="andere">Andere</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="note">Opmerking</Label>
            <Textarea id="note" data-testid="entry-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </div>

          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-accent rounded-md px-3 py-2">
            <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Na registratie is deze boeking onveranderlijk. Fouten worden gecorrigeerd via een aparte correctieboeking.
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => submit(false)} disabled={saving} data-testid="submit-entry">
            {saving ? "Bezig…" : "Definitief boeken"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={!!gapInfo} onOpenChange={(o) => !o && setGapInfo(null)}>
        <DialogContent data-testid="gap-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Vorige dag(en) nog niet ingevuld</DialogTitle>
            <DialogDescription>
              Je kan deze dag pas boeken als de vorige dag(en) ingevuld zijn. Nog niet ingevuld:
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 space-y-1">
            {(gapInfo?.missing || []).map((d) => (
              <div key={d} className="flex items-center gap-2 text-sm text-amber-800">
                <Lock className="h-3.5 w-3.5" /> GESLOTEN op <b>{d}</b> — 0 ontvangsten
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Kies "Deze dag(en) zelf invullen" om ze los te boeken, of "Markeer als GESLOTEN (€0)" om ze af te sluiten met 0 ontvangsten.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="gap-fill-self" onClick={() => setGapInfo(null)}>Deze dag(en) zelf invullen</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" data-testid="gap-auto-zero" disabled={saving} onClick={() => submit(true)}>
              {saving ? "Bezig…" : "Markeer als GESLOTEN (€0)"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default function Journal() {
  const { canWrite, isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filter, setFilter] = useState("all");
  const [inboxPending, setInboxPending] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/journal", { params: { location_id: filter } }).then((r) => setEntries(r.data));
  };
  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data));
    api.get("/ztickets/inbox/summary").then((r) => setInboxPending(r.data.pending)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [filter]);

  const downloadHistorical = async () => {
    setBusy(true);
    try {
      const r = await api.get("/dagontvangsten/historical-export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Dagontvangstenboek_historisch_2026.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Kon het historische dagontvangstenboek niet ophalen.");
    } finally { setBusy(false); }
  };

  const importHistorical = async () => {
    if (!window.confirm("Historisch dagontvangstenboek (jan-jul 2026) inladen? Bestaande boekingen in dat bereik worden vervangen door de bron-PDF (cijfers ongewijzigd).")) return;
    setBusy(true);
    try {
      const r = await api.post("/dagontvangsten/import-historical");
      if (r.data.already_imported) toast.info(`Reeds ingeladen (${r.data.count} boekingen).`);
      else toast.success(`${r.data.imported} historische boekingen ingeladen.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Import mislukt.");
    } finally { setBusy(false); }
  };

  const [closeOpen, setCloseOpen] = useState(false);
  const [closeDate, setCloseDate] = useState(new Date().toISOString().slice(0, 10));
  const [closing, setClosing] = useState(false);
  const closeDay = async () => {
    setClosing(true);
    let ok = 0, skip = 0;
    for (const l of locations) {
      try {
        await api.post("/journal?fill_gaps=true", {
          location_id: l.id, date: closeDate, entry_type: "none",
          gross_by_rate: {}, none_reason: "gesloten",
          note: `Gesloten op ${closeDate} — 0 ontvangsten`, source: "manual",
        });
        ok++;
      } catch (e) {
        const det = e.response?.data?.detail;
        if (typeof det === "string" && det.includes("bestaat al")) skip++;
        else { /* andere fout */ skip++; }
      }
    }
    setClosing(false); setCloseOpen(false);
    toast.success(`Dag ${closeDate} als GESLOTEN geboekt voor ${ok} winkel(s)${skip ? `, ${skip} overgeslagen (reeds geboekt)` : ""}.`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="journal-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Dagboek van ontvangsten</h1>
          <p className="text-sm text-muted-foreground mt-1">Chronologische, onveranderlijke registratie per vestiging</p>
        </div>
        <div className="flex gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="journal-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle vestigingen</SelectItem>
              {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <MissingDaysDialog />
          <Button variant="outline" data-testid="download-historical-book" onClick={downloadHistorical} disabled={busy}>
            <BookText className="h-4 w-4 mr-2" /> Historisch boek (PDF)
          </Button>
          {isAdmin && (
            <Button variant="outline" data-testid="import-historical" onClick={importHistorical} disabled={busy}>
              <Download className="h-4 w-4 mr-2" /> Historiek inladen
            </Button>
          )}
          {canWrite && (
            <Button asChild variant="outline" data-testid="open-zinbox" className="relative">
              <Link to="/z-inbox">
                <ScanLine className="h-4 w-4 mr-2" /> Z-tickets (gsm)
                {inboxPending > 0 && (
                  <Badge className="ml-2 bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid="zinbox-pending-badge">{inboxPending}</Badge>
                )}
              </Link>
            </Button>
          )}
          {canWrite && (
            <Button asChild variant="outline" data-testid="open-zticket">
              <Link to="/z-ticket"><ScanLine className="h-4 w-4 mr-2" /> Z-ticket OCR</Link>
            </Button>
          )}
          {canWrite && (
            <Button variant="outline" data-testid="close-day-open" onClick={() => setCloseOpen(true)}>
              <Lock className="h-4 w-4 mr-2" /> Dag sluiten
            </Button>
          )}
          {canWrite && <EntryForm locations={locations} onCreated={load} />}
        </div>
      </div>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent data-testid="close-day-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Dag sluiten (GESLOTEN)</DialogTitle>
            <DialogDescription>
              Boekt de gekozen dag als <b>GESLOTEN — 0 ontvangsten</b> voor alle vestigingen. Winkels die al een boeking hebben op die dag blijven ongewijzigd.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="closedate">Datum</Label>
            <Input id="closedate" type="date" data-testid="close-day-date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Annuleren</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" data-testid="close-day-confirm" disabled={closing} onClick={closeDay}>
              {closing ? "Bezig…" : "Markeer als GESLOTEN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Vestiging</TableHead>
                {RATE_CODES.map((c) => <TableHead key={c} className="text-right">{RATE_LABELS[c]}</TableHead>)}
                <TableHead className="text-right">Totaal incl.</TableHead>
                <TableHead className="text-right">BTW</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">Nog geen boekingen.</TableCell></TableRow>
              )}
              {entries.map((e) => {
                const corr = e.has_corrections;
                const byRate = corr
                  ? Object.fromEntries(RATE_CODES.map((c) => [c, (e.corrected_by_rate?.[c]?.gross) ?? 0]))
                  : Object.fromEntries(e.vat.lines.map((l) => [l.code || String(l.rate), l.gross]));
                const totGross = corr ? e.corrected_gross : e.vat.total_gross;
                const totVat = corr ? e.corrected_vat : e.vat.total_vat;
                return (
                  <TableRow key={e.id} data-testid={`journal-row-${e.id}`} className="hover:bg-accent/50">
                    <TableCell className="font-mono text-sm">{e.date}</TableCell>
                    <TableCell className="font-medium">{e.location_name}</TableCell>
                    {RATE_CODES.map((c) => <TableCell key={c} className="text-right tabular-nums">{eur(byRate[c])}</TableCell>)}
                    <TableCell className="text-right tabular-nums font-semibold" data-testid={`journal-total-${e.id}`}>
                      {eur(totGross)}
                      {corr && <div className="text-[11px] font-normal text-muted-foreground line-through">{eur(e.vat.total_gross)}</div>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{eur(totVat)}</TableCell>
                    <TableCell>
                      {e.entry_type === "none" ? (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">Geen ({e.none_reason})</Badge>
                      ) : e.status === "corrected" ? (
                        <Badge variant="outline" className="text-blue-600 border-blue-300" title="Bedrag hierboven is inclusief correctie(s)">Gecorrigeerd</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1">
                          <ShieldCheck className="h-3 w-3" /> Definitief
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
